import axios, { isAxiosError } from "axios";
import { buildAxiosAuthenticationHeadersForMultipart } from "../util/build-axios-authentication-headers";
import { dashboardsUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { Widget, WidgetPositioning } from "@src/types/dashboards";

/** POST a brand-new widget to a dashboard with its full body. The BE
 *  accepts the full widget spec here, so this is the only call needed for
 *  the pick-then-save flow — the BE assigns the real id and returns the
 *  committed widget on success. `id` MUST be stripped from `widget`
 *  before calling (it's server-assigned). Positioning + optional image
 *  travel as sibling multipart parts. */
export const postAddWidgetFromTemplateV2 = async (
  namespace: string,
  dashboardId: string,
  widget: Omit<Widget, "id">,
  positioning: WidgetPositioning,
  imageFile?: File | null,
): Promise<{ data: Widget }> => {
  const url = dashboardsUrlEnvVar();
  const headers = buildAxiosAuthenticationHeadersForMultipart();

  // POST has no existing image to delete or keep — we're creating a
  // brand-new widget. So all three FE intent states (File / null /
  // undefined) collapse to "append File or omit the part". This is
  // intentional asymmetry with PUT: per the drawer's docblock, the
  // FE surfaces `undefined` as a Delete signal, but on POST there's
  // nothing to delete and per the BE contract `image: null` only
  // clears an existing asset. Both `null` and `undefined` are
  // no-op here, only a `File` adds the multipart `image` part.
  const formData = new FormData();
  formData.append("widget_data", JSON.stringify(widget));
  formData.append("widget_positioning", JSON.stringify(positioning));
  if (imageFile) {
    formData.append("image", imageFile);
  }

  try {
    const response = await axios.post<Widget>(
      `${url}/api/v2/ns/${namespace}/dashboards/${dashboardId}/widgets`,
      formData,
      { headers },
    );
    return response;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error("Axios error:", err);
      throw err;
    }
    throw new Error("An unexpected error occurred while adding the widget.");
  }
};
